(() => {
  'use strict';

  const config = window.PawdelouTheme || {};
  const motionContexts = new WeakMap();

  const liveRegion = () => document.querySelector('.pv-live-region');
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function announce(message) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  function setMenuState(header, open) {
    const trigger = header.querySelector('[data-pv-menu-open]');
    const menu = header.querySelector('[data-pv-mobile-menu]');
    const backdrop = header.querySelector('.pv-mobile-menu__backdrop');
    if (!trigger || !menu || !backdrop) return;

    const canAnimate = window.gsap && !prefersReducedMotion();
    trigger.setAttribute('aria-expanded', String(open));
    window.gsap?.killTweensOf([menu, backdrop]);

    if (open) {
      menu.hidden = false;
      backdrop.hidden = false;
      document.documentElement.classList.add('pv-menu-is-open');

      if (canAnimate) {
        window.gsap
          .timeline({ defaults: { overwrite: 'auto' } })
          .fromTo(backdrop, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.18, ease: 'power1.out' }, 0)
          .fromTo(menu, { xPercent: 104 }, { xPercent: 0, duration: 0.34, ease: 'power3.out' }, 0);
      }

      const closeButton = menu.querySelector('[data-pv-menu-close]');
      closeButton?.focus();
      return;
    }

    const finishClose = () => {
      menu.hidden = true;
      backdrop.hidden = true;
      document.documentElement.classList.remove('pv-menu-is-open');
      trigger.focus();
    };

    if (canAnimate && !menu.hidden) {
      window.gsap
        .timeline({ defaults: { overwrite: 'auto' }, onComplete: finishClose })
        .to(menu, { xPercent: 104, duration: 0.22, ease: 'power2.in' }, 0)
        .to(backdrop, { autoAlpha: 0, duration: 0.16, ease: 'power1.in' }, 0.04);
    } else {
      finishClose();
    }
  }

  function initMenu(root = document) {
    const headers = root.matches?.('[data-pv-header]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-pv-header]') || []);

    headers.forEach((header) => {
      if (header.dataset.menuReady === 'true') return;
      header.dataset.menuReady = 'true';

      const openButton = header.querySelector('[data-pv-menu-open]');
      const closeButtons = header.querySelectorAll('[data-pv-menu-close]');
      const menu = header.querySelector('[data-pv-mobile-menu]');
      if (!openButton || !menu) return;

      openButton.addEventListener('click', () => setMenuState(header, true));
      closeButtons.forEach((button) => {
        button.addEventListener('click', () => setMenuState(header, false));
      });
      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => setMenuState(header, false));
      });

      header.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && openButton.getAttribute('aria-expanded') === 'true') {
          setMenuState(header, false);
          return;
        }

        if (event.key === 'Tab' && openButton.getAttribute('aria-expanded') === 'true') {
          const focusable = Array.from(
            menu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])')
          ).filter((element) => !element.hidden && element.offsetParent !== null);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (!first || !last) return;

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });
    });
  }

  async function refreshCartCount() {
    if (!config.routes?.cart) return;
    const response = await fetch(`${config.routes.cart}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart refresh failed');
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      const template =
        cart.item_count === 1
          ? config.strings?.cartCountOne || 'Cart with [count] item'
          : config.strings?.cartCountOther || 'Cart with [count] items';
      link.setAttribute('aria-label', template.replace('[count]', String(cart.item_count)));
    });
  }

  function initProductVariants(root = document) {
    const selectors = root.matches?.('.pv-product-form select[name="id"]')
      ? [root]
      : Array.from(root.querySelectorAll?.('.pv-product-form select[name="id"]') || []);

    selectors.forEach((selector) => {
      if (selector.dataset.variantSelectorReady === 'true') return;
      selector.dataset.variantSelectorReady = 'true';

      selector.addEventListener('change', () => {
        const option = selector.selectedOptions[0];
        const form = selector.closest('.pv-product-form');
        const product = selector.closest('.pv-main-product');
        if (!option || !form || !product) return;

        const available = option.dataset.available === 'true';
        const price = product.querySelector('[data-product-price]');
        const button = form.querySelector('[type="submit"]');
        const label = button?.querySelector('[data-button-label]');
        const paymentButton = form.querySelector('[data-payment-button]');
        const mediaItems = product.querySelectorAll('[data-product-media-id]');
        const featuredMediaId = option.dataset.featuredMediaId;

        if (price && option.dataset.price) price.textContent = option.dataset.price;
        const shell = selector.closest('.pv-main-shell') || document;
        const buybarPrice = shell.querySelector('[data-buybar-price]');
        const buybarBtn = shell.querySelector('[data-pv-buybar-add]');
        const buybarLabel = shell.querySelector('[data-buybar-label]');
        if (buybarPrice && option.dataset.price) buybarPrice.textContent = option.dataset.price;
        if (buybarBtn) buybarBtn.disabled = !available;
        if (buybarLabel) {
          buybarLabel.textContent = available
            ? config.strings?.add || 'Add to cart'
            : config.strings?.soldOut || 'Sold out';
        }
        if (button) button.disabled = !available;
        if (label) {
          label.textContent = available
            ? config.strings?.add || 'Add to cart'
            : config.strings?.soldOut || 'Sold out';
        }
        if (paymentButton) paymentButton.hidden = !available;

        if (featuredMediaId) {
          mediaItems.forEach((media) => {
            media.classList.toggle('is-active', media.dataset.productMediaId === featuredMediaId);
          });
        }

        if (option.dataset.url && window.history?.replaceState) {
          window.history.replaceState({}, '', option.dataset.url);
        }
      });
    });
  }

  function initProductForms(root = document) {
    const forms = root.matches?.('[data-pv-product-form]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-pv-product-form]') || []);

    forms.forEach((form) => {
      if (form.dataset.productFormReady === 'true') return;
      form.dataset.productFormReady = 'true';

      form.addEventListener('submit', async (event) => {
        if (!window.fetch || !config.routes?.cartAdd) return;
        event.preventDefault();

        const button = form.querySelector('[type="submit"]');
        const label = button?.querySelector('[data-button-label]');
        const message = form.querySelector('[data-product-form-message]');
        const originalLabel = label?.textContent;
        form.classList.remove('has-error');
        if (message) {
          message.hidden = true;
          message.textContent = '';
        }
        if (button) button.disabled = true;
        if (label) label.textContent = config.strings?.adding || 'Adding…';
        form.classList.add('is-loading');

        try {
          const response = await fetch(config.routes.cartAdd, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || config.strings?.error || 'Could not add item');
          }

          await refreshCartCount();
          if (label) label.textContent = config.strings?.added || 'Added';
          form.classList.add('is-added');
          announce(config.strings?.added || 'Added to cart');
        } catch (error) {
          const errorMessage = error.message || config.strings?.error || 'Could not add that. Try again.';
          announce(errorMessage);
          if (message) {
            message.textContent = errorMessage;
            message.hidden = false;
          }
          form.classList.add('has-error');
        } finally {
          form.classList.remove('is-loading');
          window.setTimeout(() => {
            if (label && originalLabel) label.textContent = originalLabel;
            if (button) button.disabled = false;
            form.classList.remove('is-added');
          }, 1800);
        }
      });
    });
  }

  function initDetails(root = document) {
    const details = root.matches?.('.pv-faq__item')
      ? [root]
      : Array.from(root.querySelectorAll?.('.pv-faq__item') || []);

    details.forEach((item) => {
      if (item.dataset.detailsReady === 'true') return;
      item.dataset.detailsReady = 'true';
      item.addEventListener('toggle', () => {
        item.setAttribute('data-open', String(item.open));
        if (item.open && window.gsap && !prefersReducedMotion()) {
          const answer = item.querySelector('.pv-faq__answer');
          window.gsap.fromTo(
            answer,
            { y: -8, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.26, ease: 'power2.out', overwrite: 'auto' }
          );
        }
      });
    });
  }

  function initPawPointer() {
    if (
      document.documentElement.dataset.pawPointerReady === 'true' ||
      config.features?.motion === false ||
      config.features?.pawPointer === false ||
      !window.gsap ||
      prefersReducedMotion() ||
      !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ) {
      return;
    }

    const pointer = document.querySelector('[data-pv-paw-pointer]');
    const layer = document.querySelector('[data-pv-paw-stamp-layer]');
    const paw = pointer?.querySelector('svg');
    if (!pointer || !layer || !paw) return;

    document.documentElement.dataset.pawPointerReady = 'true';
    const { gsap } = window;
    const moveX = gsap.quickTo(pointer, 'x', { duration: 0.18, ease: 'power3.out' });
    const moveY = gsap.quickTo(pointer, 'y', { duration: 0.18, ease: 'power3.out' });
    const colors = ['var(--pv-orange)', 'var(--pv-blue)', 'var(--pv-green)', 'var(--pv-pink)'];
    let interactive = false;
    let stampIndex = 0;

    gsap.set(pointer, { xPercent: -50, yPercent: -50, autoAlpha: 0 });

    document.addEventListener('pointermove', (event) => {
      moveX(event.clientX);
      moveY(event.clientY);
      gsap.to(pointer, { autoAlpha: 1, duration: 0.14, overwrite: 'auto' });

      const nextInteractive = Boolean(
        event.target.closest?.('a, button, summary, input, select, textarea, [role="button"]')
      );
      if (nextInteractive !== interactive) {
        interactive = nextInteractive;
        pointer.classList.toggle('is-interactive', interactive);
        gsap.to(pointer, {
          scale: interactive ? 1.32 : 1,
          rotation: interactive ? -10 : 0,
          duration: 0.2,
          ease: 'power2.out',
          overwrite: 'auto'
        });
      }
    });

    document.addEventListener('pointerleave', () => {
      gsap.to(pointer, { autoAlpha: 0, duration: 0.16, overwrite: 'auto' });
    });

    document.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;

      const stamp = document.createElement('span');
      stamp.className = 'pv-paw-stamp';
      stamp.style.color = colors[stampIndex % colors.length];
      stampIndex += 1;
      stamp.appendChild(paw.cloneNode(true));
      layer.appendChild(stamp);

      const rotation = gsap.utils.random(-28, 28, 1);
      const driftX = gsap.utils.random(-12, 12, 1);
      const driftY = gsap.utils.random(-20, -8, 1);

      gsap
        .timeline({ onComplete: () => stamp.remove() })
        .set(stamp, {
          x: event.clientX,
          y: event.clientY,
          xPercent: -50,
          yPercent: -50,
          rotation,
          scale: 0.35,
          autoAlpha: 0
        })
        .to(stamp, { scale: 1, autoAlpha: 0.92, duration: 0.22, ease: 'back.out(2.2)' })
        .to(stamp, { x: `+=${driftX}`, y: `+=${driftY}`, autoAlpha: 0, scale: 1.28, duration: 0.3, ease: 'power2.in' }, 0.24);

      gsap.fromTo(
        pointer,
        { scale: interactive ? 1.32 : 1 },
        {
          scale: interactive ? 1.08 : 0.78,
          duration: 0.08,
          repeat: 1,
          yoyo: true,
          ease: 'power1.inOut',
          overwrite: 'auto'
        }
      );
    });
  }

  function initAnimations(root = document) {
    if (config.features?.motion === false || !window.gsap || !window.ScrollTrigger || motionContexts.has(root)) return;

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    gsap.defaults({ duration: 0.65, ease: 'power3.out' });

    const query = gsap.utils.selector(root);
    const media = gsap.matchMedia();

    media.add(
      {
        motionOK: '(prefers-reduced-motion: no-preference)',
        finePointer: '(hover: hover) and (pointer: fine)',
        isMobile: '(max-width: 919px)'
      },
      (context) => {
        const { motionOK, finePointer, isMobile } = context.conditions;
        if (!motionOK) return;

        query('[data-pv-scroll-progress]').forEach((progress) => {
          gsap.fromTo(
            progress,
            { scaleX: 0 },
            {
              scaleX: 1,
              ease: 'none',
              scrollTrigger: {
                start: 0,
                end: 'max',
                scrub: 0.2
              }
            }
          );
        });

        query('[data-pv-hero]').forEach((hero) => {
          const lines = hero.querySelectorAll('.pv-hero__heading > span');
          const copy = hero.querySelectorAll('.pv-hero__eyebrow, .pv-hero__body, .pv-button-row, .pv-hero__facts');
          const image = hero.querySelector('.pv-hero__image');
          const sticker = hero.querySelector('.pv-hero__sticker');
          const seal = hero.querySelector('.pv-club-seal');

          const timeline = gsap.timeline({
            defaults: { ease: 'power4.out' }
          });

          timeline
            .from(lines, { yPercent: 112, autoAlpha: 0, duration: 0.72, stagger: 0.075 }, 0.05)
            .from(image, { scale: 1.1, autoAlpha: 0, duration: 1.05, ease: 'power3.inOut' }, 0.12)
            .from(copy, { y: 18, autoAlpha: 0, stagger: 0.07 }, 0.38)
            .from(sticker, { scale: 0.4, rotation: -18, autoAlpha: 0, ease: 'back.out(1.7)' }, 0.65)
            .from(seal, { scale: 0.6, rotation: 14, autoAlpha: 0, ease: 'back.out(1.5)' }, 0.76);
        });

        query('[data-pv-marquee]').forEach((track) => {
          const groups = track.children.length;
          const speed = Number(track.dataset.speed) || 24;
          const marqueeTween = gsap.to(track, {
            xPercent: -100 / groups,
            duration: speed,
            ease: 'none',
            repeat: -1
          });
          const pause = () => marqueeTween.pause();
          const play = () => marqueeTween.play();
          track.parentElement?.addEventListener('pointerenter', pause);
          track.parentElement?.addEventListener('pointerleave', play);
          track.parentElement?.addEventListener('focusin', pause);
          track.parentElement?.addEventListener('focusout', play);
        });

        const cardTargets = query('[data-pv-product-card], [data-pv-reset-card], [data-pv-promise]');
        const revealTargets = query('.pv-reveal').filter(
          (element) => !element.closest('[data-pv-hero]') && !cardTargets.includes(element)
        );
        if (revealTargets.length) {
          gsap.set(revealTargets, { y: 20, autoAlpha: 0 });
          ScrollTrigger.batch(revealTargets, {
            start: 'top 88%',
            once: true,
            interval: 0.08,
            batchMax: 4,
            onEnter: (batch) => {
              gsap.to(batch, {
                y: 0,
                autoAlpha: 1,
                duration: 0.52,
                stagger: { each: 0.055, from: 'start' },
                overwrite: 'auto'
              });
            }
          });
        }

        if (cardTargets.length) {
          gsap.set(cardTargets, { y: 32, scale: 0.965, autoAlpha: 0 });
          ScrollTrigger.batch(cardTargets, {
            start: 'top 90%',
            once: true,
            interval: 0.08,
            batchMax: 4,
            onEnter: (batch) => {
              gsap.to(batch, {
                y: 0,
                scale: 1,
                autoAlpha: 1,
                duration: 0.48,
                stagger: { each: 0.055, from: 'start', grid: 'auto' },
                ease: 'back.out(1.3)',
                overwrite: 'auto'
              });
            }
          });
        }

        query('[data-pv-problem]').forEach((problem) => {
          const faces = problem.querySelectorAll('.pv-doodle-sheet__face');
          gsap.from(faces, {
            scale: 0.72,
            autoAlpha: 0,
            duration: 0.42,
            stagger: { each: 0.035, from: 'random', grid: 'auto' },
            ease: 'back.out(1.6)',
            scrollTrigger: {
              trigger: problem.querySelector('.pv-problem__art'),
              start: 'top 78%',
              once: true
            }
          });
        });

        query('[data-pv-salon-banner]').forEach((banner) => {
          const image = banner.querySelector('.pv-salon-banner__image');
          const overlay = banner.querySelector('.pv-salon-banner__overlay');
          if (!image || !overlay) return;

          const timeline = gsap.timeline({
            scrollTrigger: {
              trigger: banner,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.8
            }
          });
          timeline
            .fromTo(image, { scale: 1.12, yPercent: -4 }, { scale: 1.02, yPercent: 4, ease: 'none' }, 0)
            .fromTo(overlay, { yPercent: 10 }, { yPercent: -5, ease: 'none' }, 0);

          gsap.from(overlay.children, {
            y: 28,
            autoAlpha: 0,
            duration: 0.58,
            stagger: 0.07,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: banner,
              start: 'top 68%',
              once: true
            }
          });
        });

        query('[data-pv-reset]').forEach((reset) => {
          const cards = Array.from(reset.querySelectorAll('[data-pv-reset-card]'));
          if (isMobile) {
            cards.forEach((card) => {
              ScrollTrigger.create({
                trigger: card,
                start: 'top 62%',
                end: 'bottom 38%',
                toggleClass: { targets: card, className: 'is-active' }
              });
            });
          } else {
            cards[0]?.classList.add('is-active');
          }
        });

        query('[data-pv-promise]').forEach((promise) => {
          const seal = promise.querySelector('.pv-promise__seal');
          if (!seal) return;
          gsap.from(seal, {
            scale: 0.3,
            rotation: -18,
            autoAlpha: 0,
            duration: 0.42,
            ease: 'back.out(1.8)',
            scrollTrigger: {
              trigger: promise,
              start: 'top 78%',
              once: true
            }
          });
        });

        if (finePointer) {
          const cleanups = [];
          query('[data-pv-product-card]').forEach((card) => {
            const art = card.querySelector('.pv-product-card__media > img, .pv-product-illustration');
            let bounds;
            const rotateXTo = gsap.quickTo(card, 'rotationX', { duration: 0.28, ease: 'power2.out' });
            const rotateYTo = gsap.quickTo(card, 'rotationY', { duration: 0.28, ease: 'power2.out' });
            const enter = () => {
              bounds = card.getBoundingClientRect();
              gsap.to(art, { y: -7, rotation: -1.2, duration: 0.28, overwrite: 'auto' });
              gsap.to(card, { y: -4, duration: 0.22, overwrite: 'auto' });
            };
            const move = (event) => {
              if (!bounds) return;
              const x = gsap.utils.clamp(-1, 1, (event.clientX - bounds.left) / bounds.width * 2 - 1);
              const y = gsap.utils.clamp(-1, 1, (event.clientY - bounds.top) / bounds.height * 2 - 1);
              rotateXTo(y * -2.2);
              rotateYTo(x * 2.2);
            };
            const leave = () => {
              gsap.to(art, { y: 0, rotation: 0, duration: 0.35, overwrite: 'auto' });
              gsap.to(card, { y: 0, rotationX: 0, rotationY: 0, duration: 0.35, overwrite: 'auto' });
            };
            card.addEventListener('mouseenter', enter);
            card.addEventListener('mousemove', move);
            card.addEventListener('mouseleave', leave);
            cleanups.push(() => {
              card.removeEventListener('mouseenter', enter);
              card.removeEventListener('mousemove', move);
              card.removeEventListener('mouseleave', leave);
            });
          });

          query('[data-pv-hero]').forEach((hero) => {
            const media = hero.querySelector('.pv-hero__media');
            const image = hero.querySelector('.pv-hero__image');
            const sticker = hero.querySelector('.pv-hero__sticker');
            if (!media || !image || !sticker) return;

            let bounds;
            const imageX = gsap.quickTo(image, 'x', { duration: 0.7, ease: 'power3.out' });
            const imageY = gsap.quickTo(image, 'y', { duration: 0.7, ease: 'power3.out' });
            const stickerX = gsap.quickTo(sticker, 'x', { duration: 0.5, ease: 'power3.out' });
            const stickerY = gsap.quickTo(sticker, 'y', { duration: 0.5, ease: 'power3.out' });
            const enter = () => {
              bounds = media.getBoundingClientRect();
            };
            const move = (event) => {
              if (!bounds) return;
              const x = gsap.utils.clamp(-1, 1, (event.clientX - bounds.left) / bounds.width * 2 - 1);
              const y = gsap.utils.clamp(-1, 1, (event.clientY - bounds.top) / bounds.height * 2 - 1);
              imageX(x * -7);
              imageY(y * -7);
              stickerX(x * 10);
              stickerY(y * 10);
            };
            const leave = () => {
              imageX(0);
              imageY(0);
              stickerX(0);
              stickerY(0);
            };
            media.addEventListener('mouseenter', enter);
            media.addEventListener('mousemove', move);
            media.addEventListener('mouseleave', leave);
            cleanups.push(() => {
              media.removeEventListener('mouseenter', enter);
              media.removeEventListener('mousemove', move);
              media.removeEventListener('mouseleave', leave);
            });
          });

          query('[data-pv-reset]').forEach((reset) => {
            const cards = Array.from(reset.querySelectorAll('[data-pv-reset-card]'));
            cards.forEach((card) => {
              const activate = () => {
                cards.forEach((item) => item.classList.toggle('is-active', item === card));
              };
              card.addEventListener('mouseenter', activate);
              cleanups.push(() => card.removeEventListener('mouseenter', activate));
            });
          });

          return () => cleanups.forEach((cleanup) => cleanup());
        }
      },
      root === document ? document.documentElement : root
    );

    motionContexts.set(root, media);
  }

  function destroyAnimations(root) {
    const media = motionContexts.get(root);
    if (!media) return;
    media.revert();
    motionContexts.delete(root);
  }

  function init(root = document) {
    initMenu(root);
    initProductVariants(root);
    initProductForms(root);
    initDetails(root);
    if (root === document) initPawPointer();
    initAnimations(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.addEventListener('load', () => {
    window.ScrollTrigger?.refresh();
  });

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    window.ScrollTrigger?.refresh();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAnimations(event.target);
  });
})();

  /* --- Hero headline fit-to-width: guarantees the headline never clips,
         regardless of viewport width or merchant-chosen display font. --- */
  (function () {
    var fitHero = function () {
      document.querySelectorAll('[data-pv-hero] .pv-hero__heading').forEach(function (heading) {
        heading.style.fontSize = '';
        var box = heading.clientWidth;
        if (!box) return;
        var widest = 0;
        heading.querySelectorAll(':scope > span').forEach(function (line) {
          var prev = line.style.width;
          line.style.width = 'max-content';
          widest = Math.max(widest, line.getBoundingClientRect().width);
          line.style.width = prev;
        });
        if (widest > box) {
          var current = parseFloat(getComputedStyle(heading).fontSize);
          heading.style.fontSize = Math.floor(current * (box / widest) * 100) / 100 + 'px';
        }
      });
    };
    var raf;
    var schedule = function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(fitHero); };
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    schedule();
  })();

  /* --- Sticky mobile buy bar: bottom-anchored add-to-cart that appears once
         the main buy button scrolls out of view. Reuses the existing product
         form (AJAX add-to-cart + variant logic) via requestSubmit(). --- */
  (function () {
    var mq = window.matchMedia('(max-width: 920px)');

    var setup = function (bar) {
      if (bar.dataset.buybarReady === 'true') return;
      bar.dataset.buybarReady = 'true';

      var product = bar.closest('.pv-main-product-section') || bar.closest('section') || document;
      var form = product.querySelector('[data-pv-product-form]');
      var mainBtn = form ? form.querySelector('[type="submit"]') : null;
      var addBtn = bar.querySelector('[data-pv-buybar-add]');
      if (!form || !mainBtn || !addBtn) return;

      addBtn.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });

      var show = function (on) {
        if (!mq.matches) { bar.hidden = true; bar.classList.remove('is-visible'); return; }
        bar.hidden = false;
        // next frame so the transition runs
        requestAnimationFrame(function () { bar.classList.toggle('is-visible', on); });
      };

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { show(!e.isIntersecting); });
        }, { rootMargin: '0px 0px -20% 0px', threshold: 0 });
        io.observe(mainBtn);
      }

      mq.addEventListener('change', function () {
        if (!mq.matches) { bar.hidden = true; bar.classList.remove('is-visible'); }
      });
    };

    var init = function (root) {
      (root || document).querySelectorAll('[data-pv-buybar]').forEach(setup);
    };
    init(document);
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  })();

  /* --- Gallery pagination dots: native carousel affordance for the mobile
         product gallery scroller. Mobile-only; harmless on desktop. --- */
  (function () {
    var setup = function (gallery) {
      if (gallery.dataset.galleryDotsReady === 'true') return;
      gallery.dataset.galleryDotsReady = 'true';

      var scope = gallery.closest('.pv-product-gallery-wrap') || gallery.parentElement || gallery;
      var dotsWrap = scope.querySelector('[data-product-gallery-dots]');
      var items = Array.prototype.slice.call(
        gallery.querySelectorAll('.pv-product-gallery__item')
      );
      if (!dotsWrap || items.length < 2) return;

      var dots = items.map(function (item, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pv-product-gallery__dot';
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
        dot.addEventListener('click', function () {
          item.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        });
        dotsWrap.appendChild(dot);
        return dot;
      });

      var sync = function () {
        var mid = gallery.scrollLeft + gallery.clientWidth / 2;
        var active = 0;
        items.forEach(function (item, i) {
          if (item.offsetLeft <= mid) active = i;
        });
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === active); });
      };

      var raf;
      gallery.addEventListener('scroll', function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(sync);
      }, { passive: true });
      sync();
    };

    var init = function (root) {
      (root || document).querySelectorAll('[data-product-gallery]').forEach(setup);
    };
    init(document);
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  })();
